import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@src/redux/hooks';
import { store } from '@src/redux/index';
import { FJ_COMMON_VIEW, setActiveView } from '@src/redux/slice/FJCommonSlice';
import { hideDetailsPage } from '@src/redux/slice/FJDetailsPageSlice';
import { FJDetailsPageAction } from '@src/redux/action/FJDetailsPageAction';
import FJProjectStore from '@src/store/FJProjectStore';
import FJBrowserHistoryStore, { type FJBrowserHistoryState } from '@src/store/FJBrowserHistoryStore';
import { LTS_DETAILS_ENTRY } from '@src/util/FJLongToShortEa';

function resolveProjectIdForHistory(): string | undefined {
    return (
        FJProjectStore.getProjectId() ??
        store.getState().startLanding.projects.selectedProjectId ??
        undefined
    );
}

/**
 * 仅同步详情页 ↔ 首页的浏览器 history。
 * 设置 / 生成中 / 失败页切换不写 history。
 */
export default function useActiveViewHistory() {
    const dispatch = useAppDispatch();
    const activeView = useAppSelector(state => state.common.activeView);
    const hasInitializedRef = useRef(false);
    const prevViewRef = useRef(activeView);

    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            if (FJBrowserHistoryStore.consumeRevertingNavigation()) {
                return;
            }

            const historyState =
                event.state != null && typeof event.state === 'object'
                    ? (event.state as FJBrowserHistoryState)
                    : FJBrowserHistoryStore.getHistoryState();
            if (!historyState || !FJBrowserHistoryStore.isTrackedHistoryView(historyState.activeView)) {
                return;
            }

            const currentView = store.getState().common.activeView;
            if (!FJBrowserHistoryStore.isHistoryNavigationView(currentView)) {
                FJBrowserHistoryStore.revertIgnoredPopState(historyState);
                return;
            }

            FJBrowserHistoryStore.markPopStateNavigation();

            if (historyState.activeView === FJ_COMMON_VIEW.DETAILS) {
                if (historyState.projectId) {
                    const detailsState = store.getState().detailsPage;
                    if (detailsState.items.length === 0) {
                        const item = store
                            .getState()
                            .startLanding.projects.items.find(i => i.id === historyState.projectId);
                        if (item) {
                            void dispatch(
                                FJDetailsPageAction.openDetailsAction(item, false, LTS_DETAILS_ENTRY.RECENT_PROJECT),
                            );
                            return;
                        }
                    }
                }
                if (currentView !== FJ_COMMON_VIEW.DETAILS) {
                    dispatch(setActiveView(FJ_COMMON_VIEW.DETAILS));
                }
                return;
            }

            if (historyState.activeView === FJ_COMMON_VIEW.START_LANDING) {
                if (currentView === FJ_COMMON_VIEW.DETAILS) {
                    dispatch(hideDetailsPage());
                }
                if (currentView !== FJ_COMMON_VIEW.START_LANDING) {
                    dispatch(setActiveView(FJ_COMMON_VIEW.START_LANDING));
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [dispatch]);

    useEffect(() => {
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            FJBrowserHistoryStore.init();
            prevViewRef.current = activeView;
            return;
        }

        if (FJBrowserHistoryStore.consumePopStateNavigation()) {
            prevViewRef.current = activeView;
            return;
        }

        const prevView = prevViewRef.current;
        prevViewRef.current = activeView;

        if (activeView === FJ_COMMON_VIEW.DETAILS && prevView !== FJ_COMMON_VIEW.DETAILS) {
            if (FJBrowserHistoryStore.getHistoryState()?.activeView !== FJ_COMMON_VIEW.DETAILS) {
                FJBrowserHistoryStore.pushDetails(resolveProjectIdForHistory());
            }
        }
    }, [activeView]);
}
